'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import Navigation from '../../../components/Navigation';
import {
    DocAnswer,
    DocBeforeAfter,
    DocCode,
    DocEyebrow,
    DocMistakeList,
    DocRelated,
    DocSection,
    DocSubtitle,
    DocText,
    DocTitle,
} from '../../../components/DocArticle';
import { encodeBase64 } from '../../../utils/base64';

const generateUrl = (() => {
    const oldEncoded = encodeBase64(JSON.stringify({
        user: { name: 'Ana', address: { city: 'NYC', zip: '10001' } },
    }, null, 2));
    const newEncoded = encodeBase64(JSON.stringify({
        user: { name: 'Ana', address: { city: 'Boston', zip: '10001' } },
    }, null, 2));
    return `/?old=${oldEncoded}&new=${newEncoded}`;
})();

const mistakes = [
    {
        mistake: 'Assuming add creates the intermediate keys',
        fix: 'add creates only the final step of a path. /user/address/city fails when /user/address does not exist - create the parents with earlier add operations first.',
    },
    {
        mistake: 'Addressing a child after removing its parent',
        fix: 'Operations run in order. After remove /user, any path starting with /user fails. Remove the parent last, or remove only the child keys you actually want gone.',
    },
    {
        mistake: 'Replacing a whole object when you meant one property',
        fix: 'replace /user/address swaps the entire address object - every key you omit disappears. To change one property, target it: replace /user/address/city.',
    },
    {
        mistake: 'Forgetting arrays inside nested objects',
        fix: 'A path like /users/0/tags/- mixes object keys and array indexes freely - but each segment is checked against the container at that level. An object key where an array sits (or vice versa) fails.',
    },
];

export default function JsonPatchNestedObjectsPage() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
                    {/* Breadcrumb */}
                    <Box component="nav" sx={{ display: 'flex', mb: 4, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                        <Link href="/" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Home</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Link href="/docs" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Documentation</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Typography component="span" sx={{ color: 'var(--fg)', fontSize: '0.875rem' }}>JSON Patch and Nested Objects</Typography>
                    </Box>

                    <Box sx={{ maxWidth: '800px' }}>
                        <DocEyebrow>Documentation</DocEyebrow>
                        <DocTitle>JSON Patch and Nested Objects</DocTitle>
                        <DocSubtitle>
                            Real documents are deep. Updating a nested JSON property with JSON Patch means walking the
                            path segment by segment - and knowing the few rules that decide whether a deep path works.
                        </DocSubtitle>

                        <DocAnswer>
                            To update a nested property with JSON Patch, aim any operation at the full{' '}
                            <Link href="/docs/json-pointer" style={{ color: 'var(--accent)' }}>JSON Pointer</Link> path:{' '}
                            <code>{'{ "op": "replace", "path": "/user/address/city", "value": "Boston" }'}</code>.
                            Depth changes nothing about the operations - only about the path - but every segment
                            before the last one must already exist.
                        </DocAnswer>

                        <DocSection>Updating a nested property</DocSection>
                        <DocCode label="Patch">{`[
  { "op": "replace", "path": "/user/address/city", "value": "Boston" }
]`}</DocCode>
                        <DocBeforeAfter
                            before={`{
  "user": {
    "name": "Ana",
    "address": { "city": "NYC", "zip": "10001" }
  }
}`}
                            after={`{
  "user": {
    "name": "Ana",
    "address": { "city": "Boston", "zip": "10001" }
  }
}`}
                        />
                        <DocText>
                            The pointer walks <code>user</code>, then <code>address</code>, then <code>city</code>.
                            Everything around the path - <code>name</code>, <code>zip</code> - is untouched.
                        </DocText>

                        <DocSection>Adding deep: parents must exist</DocSection>
                        <DocText>
                            <code>add</code> can create the value at the end of a path, but not the containers on the
                            way. To add <code>/user/address/country</code> when <code>address</code> exists, one
                            operation is enough. When it does not, build the structure first:
                        </DocText>
                        <DocCode label="Patch">{`[
  { "op": "add", "path": "/user/address", "value": {} },
  { "op": "add", "path": "/user/address/city", "value": "Boston" },
  { "op": "add", "path": "/user/address/zip", "value": "02108" }
]`}</DocCode>
                        <DocText>
                            You can also add the whole subtree in one operation -
                            <code> add /user/address</code> with the full object as the value. Both are correct; the
                            step-by-step version is easier to guard with <code>test</code>.
                        </DocText>

                        <DocSection>Nested arrays</DocSection>
                        <DocText>
                            Object keys and array indexes mix freely in one path. Append to an array two levels down,
                            or update a field of one array element:
                        </DocText>
                        <DocCode label="Patch">{`[
  { "op": "add", "path": "/user/tags/-", "value": "vip" },
  { "op": "replace", "path": "/users/0/email", "value": "new@example.com" }
]`}</DocCode>
                        <DocText>
                            All the array rules - <code>/-</code>, index shifting, no wildcards - apply at any depth.
                            See <Link href="/docs/json-patch-array" style={{ color: 'var(--accent)' }}>JSON Patch and arrays</Link>.
                        </DocText>

                        <DocSection>Moving and copying whole subtrees</DocSection>
                        <DocText>
                            <code>move</code> and <code>copy</code> work on entire subtrees, not just scalars. Moving a
                            nested object relocates everything under it:
                        </DocText>
                        <DocCode label="Patch">{`[
  { "op": "move", "from": "/user/address", "path": "/shipping/address" }
]`}</DocCode>
                        <DocText>
                            After this, <code>/user/address</code> no longer exists - any later operation pointing
                            there fails. And the destination can never be inside the source: moving <code>/user</code>{' '}
                            to <code>/user/profile</code> is rejected. The same goes for <code>remove</code>: removing
                            a parent takes the whole subtree with it.
                        </DocText>

                        <DocSection>Guarding deep updates with test</DocSection>
                        <DocText>
                            The deeper the path, the more a document can drift from your assumption. A{' '}
                            <Link href="/operations/test" style={{ color: 'var(--accent)' }}>test</Link> on the nested
                            value fails the patch instead of writing into the wrong state:
                        </DocText>
                        <DocCode label="Patch">{`[
  { "op": "test", "path": "/user/address/zip", "value": "10001" },
  { "op": "replace", "path": "/user/address/city", "value": "Boston" }
]`}</DocCode>

                        <DocSection>Common mistakes</DocSection>
                        <DocMistakeList items={mistakes} />

                        <DocSection>Reference</DocSection>
                        <DocText>
                            Path evaluation is defined by{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6901#section-4" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 6901, section 4</a>,
                            and the parent-existence rule for add by{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6902#section-4.1" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 6902, section 4.1</a>.
                        </DocText>

                        {/* CTA */}
                        <Box sx={{
                            mt: 6, p: 4, textAlign: 'center',
                            borderRadius: 'var(--radius-lg)',
                            background: 'var(--accent)', color: 'white',
                        }}>
                            <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 2 }}>
                                Generate a nested patch yourself
                            </Typography>
                            <Typography sx={{ mb: 3, opacity: 0.9 }}>
                                Open the generator with a nested before/after preloaded - the patch it emits uses the full deep path.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                href={generateUrl}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    backgroundColor: 'white', color: 'var(--accent)', fontWeight: 600,
                                    px: 4, py: 1.5, textTransform: 'none',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Open the generator with this example
                            </Button>
                        </Box>

                        <DocRelated links={[
                            { href: '/docs/json-pointer', title: 'JSON Pointer', description: 'The segment-by-segment path syntax' },
                            { href: '/docs/json-patch-array', title: 'JSON Patch and arrays', description: 'Indexes and /- at any depth' },
                            { href: '/operations/add', title: 'Add operation', description: 'Creating the final step of a path' },
                            { href: '/docs/json-patch-errors', title: 'JSON Patch errors', description: 'When deep paths fail and why' },
                        ]} />
                    </Box>
                </Container>
            </main>
        </>
    );
}
